import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { apiGet, apiPost } from "../lib/api";
import { Check, Crown, Loader2, Flame, AlertCircle, ShieldCheck, Sparkles, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import MotionButton from "../components/motion/MotionButton";
import usePaymentPolling from "../hooks/usePaymentPolling";
import { SPRING_CARD_HOVER, FADE_UP } from "../lib/motionVariants";
import { TermsCheckbox } from "../components/TermsModal";
import { getI18n } from "../lib/i18n";

const COLOSSEUM = "https://images.pexels.com/photos/30252016/pexels-photo-30252016.jpeg";

const FEATURES_FREE = [
  "Accesso a domande base (free)",
  "Max 10 domande per simulazione · 3 tentativi",
  "Statistiche essenziali",
];
const FEATURES_PREMIUM = [
  "1.500+ domande Premium curate",
  "Modo Esame illimitato (fino a 200 domande)",
  "Tutor AI GPT-5.2 illimitato",
  "Statistiche avanzate per specialità",
];
const FEATURES_PLUS = [
  "Tutto del piano Premium",
  "+500 domande IMPOSSIBILI ad alto livello di tranello",
  "Casi clinici contro-intuitivi per il top 5% dei candidati",
  "Badge Premium Plus sul profilo",
  "Supporto prioritario diretto",
];

// Premium Ultimate — el plan VIP supremo. Diseño rojo+dorado.
const FEATURES_ULTIMATE = [
  "Tutte le 105 lezioni Academy con audio HD",
  "Tutti i simulatori scritti illimitati per 1 anno",
  "Oral Coach IA illimitato",
  "Simulazioni orali illimitate (5 slot · ricarica continua)",
  "Pacchetto AIMS · 21 manuali di specialità",
  "Casi clinici integrati · accesso totale",
  "Tutti i moduli premium senza blocchi",
  "Aggiornamenti futuri di tutto l'anno",
  "Priorità su nuovi contenuti rilasciati",
  "Esperienza completa · zero paywall interni",
];

// Per la comparativa di valore (stima realistica · annualizzata).
const VALUE_ITEMS = [
  { label: "Abbonamento Premium Plus", retail: "€22,49 × 12 = €269,88" },
  { label: "Accesso Academia 2026", retail: "€499,90" },
  { label: "Pacchetto 5 tentativi simulatore × 4", retail: "€49,90 × 4 = €199,60" },
  { label: "Pacchetto Coach Orale × 5", retail: "€79,90 × 5 = €399,50" },
  { label: "Pacchetto 10 tentativi orali × 4", retail: "€49,90 × 4 = €199,60" },
  { label: "Pacchetto AIMS · 21 manuali", retail: "€9,90" },
  { label: "Materiali premium · aggiornamenti", retail: "€350,00" },
];
const VALUE_TOTAL_LABEL = "≈ €1.928,38";
const ULTIMATE_PRICE_LABEL = "€999,90";

const ACCENT_MAP = {
  neutral: { text: "text-brand-secondary",  border: "border-brand-border",   bullet: "text-brand-secondary",   button: "secondary" },
  green:   { text: "text-brand-green",      border: "border-brand-green/40", bullet: "text-brand-green",       button: "primary"   },
  red:     { text: "text-brand-red",        border: "border-brand-red/40",   bullet: "text-brand-red",         button: "red"       },
};
const HOVER_LIFT_4 = { y: -4 };

const resolveLevel = (user) => user?.subscription_level || (user?.is_premium ? "premium" : "free");
const isUltimateUser = (user) => Boolean(user?.is_ultimate && user?.ultimate_active);

const premiumButtonLabel = (level) =>
  (level === "premium" || level === "premium_plus") ? "Già attivo" : "Sottoscrivi Premium";

const plusButtonLabel = (level, ultimate) => {
  if (ultimate) return "Già incluso in Ultimate";
  if (level === "premium_plus") return "Già attivo";
  if (level === "premium") return "Upgrade a Plus";
  return "Sottoscrivi Plus";
};

function PlanCard({
  testId, title, price, period, features, accent, badge,
  buttonLabel, onCheckout, disabled, loading, highlighted,
}) {
  const a = ACCENT_MAP[accent] || ACCENT_MAP.neutral;
  return (
    <motion.div
      whileHover={HOVER_LIFT_4}
      transition={SPRING_CARD_HOVER}
      className={`relative rounded-2xl p-7 backdrop-blur-xl bg-white/85 border ${a.border} ${
        highlighted ? "ring-2 ring-brand-gold/60 shadow-premium-lg" : "shadow-premium"
      }`}
    >
      {badge && (
        <div className="absolute -top-3 left-5 px-3 py-1 eyebrow text-[10px] rounded-full bg-brand-primary text-white shadow-premium">
          {badge}
        </div>
      )}
      <div className={`eyebrow ${a.text}`}>// {title}</div>
      <div className="mt-3 flex items-baseline gap-2">
        <div className="font-serif text-5xl font-semibold text-brand-primary">€{price}</div>
        {period && <div className="text-sm text-brand-secondary">{period}</div>}
      </div>
      <ul className="mt-6 space-y-2.5 text-sm">
        {features.map((f) => (
          <li key={f} className="flex gap-2.5 items-start text-brand-primary/85">
            <Check className={`h-4 w-4 mt-0.5 ${a.bullet} shrink-0`} />
            <span className="leading-relaxed">{f}</span>
          </li>
        ))}
      </ul>
      {onCheckout && (
        <MotionButton
          data-testid={testId}
          variant={a.button}
          size="md"
          className="mt-7 w-full"
          onClick={onCheckout}
          disabled={disabled}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
        </MotionButton>
      )}
    </motion.div>
  );
}

// Dedicated luxury card for Premium Ultimate · Accesso Totale.
// Visual hierarchy: deep red + gold border + ribbon, sits ABOVE the regular grid.
function UltimateCard({ plan, ultimateLinkAvailable, onCheckout, loading, disabled, alreadyOwned }) {
  if (!plan) return null;
  return (
    <motion.div
      data-testid="ultimate-card"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="relative overflow-hidden rounded-3xl p-8 sm:p-10 mb-10 shadow-premium-lg"
      style={{
        background:
          "linear-gradient(135deg, #1A0508 0%, #3B0A12 38%, #6F1124 72%, #2A0508 100%)",
        border: "1.5px solid rgba(212, 175, 55, 0.55)",
        boxShadow:
          "0 24px 80px -20px rgba(89, 5, 18, 0.55), 0 0 0 1px rgba(212, 175, 55, 0.18) inset",
      }}
    >
      {/* Decorative grain */}
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 10%, rgba(255,215,140,0.45) 0%, transparent 35%), radial-gradient(circle at 80% 85%, rgba(255,200,120,0.35) 0%, transparent 40%)",
        }}
      />

      {/* Ribbon */}
      <div className="absolute -right-12 top-6 rotate-45 px-14 py-1.5 text-[10px] tracking-[0.32em] font-semibold text-[#1A0508]"
           style={{ background: "linear-gradient(90deg, #E9C779 0%, #F4DA9A 50%, #C99B3B 100%)" }}>
        ACCESSO TOTALE
      </div>

      <div className="relative flex flex-col lg:flex-row lg:items-stretch gap-8">
        {/* Left: identity + price */}
        <div className="lg:w-1/2">
          <div className="flex items-center gap-2 text-[11px] tracking-[0.32em]"
               style={{ color: "#E9C779" }}>
            <Sparkles className="h-3.5 w-3.5" />
            BEST VALUE · RECOMMANDATO
          </div>
          <h2 className="mt-3 font-serif text-5xl sm:text-6xl leading-tight text-white">
            Premium <em className="italic-display" style={{ color: "#F4DA9A" }}>Ultimate</em>
          </h2>
          <p className="mt-3 text-base sm:text-lg" style={{ color: "rgba(255,235,200,0.85)" }}>
            Sblocchi assolutamente tutte le funzioni dell&apos;ecosistema premium con un unico pagamento.
            <br className="hidden sm:block" />
            <span style={{ color: "#F4DA9A" }}>365 giorni · zero blocchi · zero paywall interni.</span>
          </p>

          <div className="mt-7 flex items-baseline gap-3">
            <div className="font-serif text-6xl font-semibold" style={{ color: "#F4DA9A" }}>
              {ULTIMATE_PRICE_LABEL}
            </div>
            <div className="text-sm" style={{ color: "rgba(255,235,200,0.65)" }}>
              pagamento unico
            </div>
          </div>
          <div className="mt-1 text-[11px] line-through" style={{ color: "rgba(255,235,200,0.5)" }}>
            valore reale {VALUE_TOTAL_LABEL}
          </div>

          <div className="mt-7">
            {alreadyOwned ? (
              <div data-testid="ultimate-already-owned"
                   className="inline-flex items-center gap-2 px-5 py-3 rounded-xl"
                   style={{ background: "rgba(244,218,154,0.15)", color: "#F4DA9A",
                            border: "1px solid rgba(212,175,55,0.4)" }}>
                <Crown className="h-5 w-5" /> Sei già Premium Ultimate
              </div>
            ) : (
              <button
                data-testid="ultimate-checkout-btn"
                onClick={onCheckout}
                disabled={disabled}
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-base sm:text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  background: ultimateLinkAvailable
                    ? "linear-gradient(90deg, #E9C779 0%, #F4DA9A 45%, #C99B3B 100%)"
                    : "rgba(255,235,200,0.18)",
                  color: ultimateLinkAvailable ? "#1A0508" : "rgba(255,235,200,0.7)",
                  border: ultimateLinkAvailable
                    ? "1px solid rgba(212,175,55,0.6)"
                    : "1px solid rgba(255,235,200,0.25)",
                  boxShadow: ultimateLinkAvailable
                    ? "0 12px 32px -8px rgba(212,175,55,0.55)"
                    : "none",
                }}
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : ultimateLinkAvailable ? (
                  <>
                    <Crown className="h-5 w-5" />
                    Sblocca l&apos;accesso totale
                  </>
                ) : (
                  <>Disponibile a breve · contattaci</>
                )}
              </button>
            )}
            {!ultimateLinkAvailable && !alreadyOwned ? (
              <p className="mt-3 text-[11.5px]" style={{ color: "rgba(255,235,200,0.6)" }}>
                Stiamo finalizzando la configurazione di pagamento. Scriveteci a{" "}
                <span style={{ color: "#F4DA9A" }}>drpabloterzano@gmail.com</span> per acquistare ora.
              </p>
            ) : null}
          </div>
        </div>

        {/* Right: benefits list */}
        <div className="lg:w-1/2 lg:border-l lg:pl-8"
             style={{ borderColor: "rgba(212,175,55,0.2)" }}>
          <div className="text-[11px] tracking-[0.32em]" style={{ color: "#E9C779" }}>
            INCLUSO PER 12 MESI
          </div>
          <ul className="mt-4 space-y-2.5 text-sm" data-testid="ultimate-features">
            {FEATURES_ULTIMATE.map((f) => (
              <li key={f} className="flex gap-2.5 items-start" style={{ color: "rgba(255,235,200,0.9)" }}>
                <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#F4DA9A" }} />
                <span className="leading-relaxed">{f}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

// "Lo stesso ti costerebbe…" — visual proof of value.
function ValueBreakdown() {
  return (
    <motion.div
      data-testid="ultimate-value-breakdown"
      initial={FADE_UP.hidden}
      whileInView={FADE_UP.show}
      viewport={{ once: true, margin: "-80px" }}
      className="mb-10 rounded-2xl overflow-hidden border bg-white/90 backdrop-blur shadow-premium"
      style={{ borderColor: "rgba(212,175,55,0.35)" }}
    >
      <div className="px-6 py-4 flex items-center gap-3"
           style={{ background: "linear-gradient(90deg, #FDF6E3 0%, #F8E9C4 100%)",
                    borderBottom: "1px solid rgba(212,175,55,0.25)" }}>
        <Sparkles className="h-4 w-4" style={{ color: "#9B6A1A" }} />
        <h3 className="font-serif text-lg" style={{ color: "#5B3B07" }}>
          Cosa pagheresti acquistando tutto separatamente
        </h3>
      </div>
      <div className="divide-y" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
        {VALUE_ITEMS.map((it) => (
          <div key={it.label} className="flex justify-between px-6 py-3 text-sm">
            <span className="text-brand-primary/85">{it.label}</span>
            <span className="text-brand-secondary tabular-nums">{it.retail}</span>
          </div>
        ))}
        <div className="flex justify-between px-6 py-4"
             style={{ background: "rgba(212,175,55,0.08)" }}>
          <span className="font-serif text-base text-brand-primary">Totale separato</span>
          <span className="font-serif text-base tabular-nums" style={{ color: "#7A1428" }}>
            {VALUE_TOTAL_LABEL}
          </span>
        </div>
        <div className="flex justify-between px-6 py-4"
             style={{ background: "linear-gradient(90deg, #2A0508 0%, #6F1124 100%)" }}>
          <span className="font-serif text-base text-white inline-flex items-center gap-2">
            <Flame className="h-4 w-4" style={{ color: "#F4DA9A" }} />
            Con Premium Ultimate
          </span>
          <span className="font-serif text-lg font-semibold tabular-nums"
                style={{ color: "#F4DA9A" }}>
            {ULTIMATE_PRICE_LABEL} <span className="text-[11px] opacity-80">una sola volta</span>
          </span>
        </div>
      </div>
      <div className="px-6 py-3 text-[11px] text-brand-secondary text-center"
           style={{ background: "#FAFAFE", borderTop: "1px solid rgba(212,175,55,0.18)" }}>
        Risparmio stimato di oltre <strong style={{ color: "#7A1428" }}>€900</strong> rispetto all&apos;acquisto frazionato.
        Valori orientativi sui prezzi attuali · IVA inclusa ove applicabile.
      </div>
    </motion.div>
  );
}

function PaymentStatusBanner({ polling, statusMsg }) {
  if (polling) {
    return (
      <div className="mb-6 border border-brand-gold/40 bg-brand-gold/8 rounded-xl p-4 flex items-center gap-3 text-brand-primary">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">{statusMsg || "Verifica del pagamento..."}</span>
      </div>
    );
  }
  if (!statusMsg) return null;
  return (
    <div data-testid="premium-status-msg" className="mb-6 border border-brand-green/40 bg-brand-green/8 rounded-xl p-4 text-brand-primary text-sm">
      {statusMsg}
    </div>
  );
}

function ExhaustedNotice({ message }) {
  if (!message) return null;
  return (
    <div data-testid="trial-exhausted-msg" className="mb-6 border border-brand-red/40 bg-brand-red/8 rounded-xl p-4 flex items-start gap-3">
      <AlertCircle className="h-5 w-5 text-brand-red mt-0.5 shrink-0" />
      <div className="text-sm text-brand-primary">{message}</div>
    </div>
  );
}

function LevelBadge({ level, ultimate }) {
  if (ultimate) {
    return (
      <div className="mb-6 border rounded-xl p-4 flex items-center gap-3"
           style={{
             background: "linear-gradient(90deg, rgba(244,218,154,0.18) 0%, rgba(212,175,55,0.12) 100%)",
             borderColor: "rgba(212,175,55,0.45)",
           }}>
        <Crown className="h-5 w-5" style={{ color: "#9B6A1A" }} />
        <span className="text-sm text-brand-primary">
          Sei <strong style={{ color: "#7A1428" }}>Premium Ultimate</strong>. Hai accesso totale a tutto l&apos;ecosistema · grazie!
        </span>
      </div>
    );
  }
  if (level === "premium") {
    return (
      <div className="mb-6 border border-brand-green/40 bg-brand-green/8 rounded-xl p-4 flex items-center gap-3 text-brand-primary">
        <Crown className="h-5 w-5 text-brand-green" />
        <span className="text-sm">Sei Premium. Vuoi sbloccare le domande IMPOSSIBILI? Passa a Premium Plus.</span>
      </div>
    );
  }
  if (level === "premium_plus") {
    return (
      <div className="mb-6 border border-brand-red/40 bg-brand-red/8 rounded-xl p-4 flex items-center gap-3 text-brand-primary">
        <Flame className="h-5 w-5 text-brand-red" />
        <span className="text-sm">Sei Premium Plus. Hai accesso a tutto, incluse le 500 domande Impossibili.</span>
      </div>
    );
  }
  return null;
}

function PricingGrid({ plans, level, creating, onCheckout, ultimate }) {
  const premiumPlan = plans?.monthly;
  const plusPlan = plans?.plus_monthly;
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <PlanCard
        title="Free"
        price="0"
        period="per sempre · 30 domande max"
        features={FEATURES_FREE}
        accent="neutral"
      />
      {premiumPlan && (
        <PlanCard
          testId="checkout-monthly-btn"
          title="Premium"
          price={premiumPlan.amount}
          period="/mese"
          features={FEATURES_PREMIUM}
          accent="green"
          badge="Più scelto"
          highlighted={true}
          buttonLabel={ultimate ? "Già incluso in Ultimate" : premiumButtonLabel(level)}
          loading={creating === "monthly"}
          disabled={!!creating || ultimate || level === "premium" || level === "premium_plus"}
          onCheckout={() => onCheckout("monthly")}
        />
      )}
      {plusPlan && (
        <PlanCard
          testId="checkout-plus_monthly-btn"
          title="Premium Plus"
          price={plusPlan.amount}
          period="/mese"
          features={FEATURES_PLUS}
          accent="red"
          badge="🔥 Top 5%"
          buttonLabel={plusButtonLabel(level, ultimate)}
          loading={creating === "plus_monthly"}
          disabled={!!creating || ultimate || level === "premium_plus"}
          onCheckout={() => onCheckout("plus_monthly")}
        />
      )}
    </div>
  );
}

export default function Premium() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const exhaustedMessage = location.state?.exhaustedMessage;
  const [plans, setPlans] = useState(null);
  const [creating, setCreating] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const locale = user?.preferred_language === "es" ? "es" : "it";
  const ti18n = getI18n(locale).terms;

  useEffect(() => { apiGet("/plans").then(setPlans); }, []);

  const { polling, statusMsg } = usePaymentPolling({ params, refresh, navigate });

  const checkout = async (planId) => {
    if (!termsAccepted) {
      alert(ti18n.block_until_accept);
      return;
    }
    setCreating(planId);
    try {
      const r = await apiPost("/checkout/session", { plan_id: planId, origin_url: window.location.origin });
      if (r.url) window.location.href = r.url;
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    } finally { setCreating(null); }
  };

  const level = resolveLevel(user);
  const ultimate = isUltimateUser(user);
  const ultimatePlan = plans?.premium_ultimate;
  const ultimateLinkAvailable = Boolean(plans?.premium_ultimate_link_available);

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <img src={COLOSSEUM} alt="" className="h-full w-full object-cover" aria-hidden="true" />
        <div className="absolute inset-0 bg-brand-background/80 backdrop-blur-2xl" />
      </div>

      <div className="px-6 sm:px-10 py-10 lg:py-16 max-w-6xl mx-auto">
        <motion.div
          initial={FADE_UP.hidden}
          animate={FADE_UP.show}
          className="text-center mb-10"
        >
          <div className="eyebrow text-brand-red">// Piani</div>
          <h1 className="heading-display text-5xl sm:text-6xl text-brand-primary mt-2">
            Sblocca tutto il <em className="italic-display text-brand-green">potenziale</em>
          </h1>
          <p className="text-base text-brand-secondary mt-4 max-w-2xl mx-auto leading-relaxed">
            1.500+ domande Premium · 500 IMPOSSIBILI · Tutor AI GPT-5.2 · 105 lezioni Academy con audio HD
          </p>
        </motion.div>

        <ExhaustedNotice message={exhaustedMessage} />
        <PaymentStatusBanner polling={polling} statusMsg={statusMsg} />
        <LevelBadge level={level} ultimate={ultimate} />

        <UltimateCard
          plan={ultimatePlan}
          ultimateLinkAvailable={ultimateLinkAvailable}
          alreadyOwned={ultimate}
          loading={creating === "premium_ultimate"}
          disabled={!!creating || !ultimateLinkAvailable || ultimate}
          onCheckout={() => checkout("premium_ultimate")}
        />

        <ValueBreakdown />

        <div className="text-center mb-6">
          <div className="eyebrow text-brand-secondary">// Altri piani</div>
          <p className="text-sm text-brand-secondary mt-1">
            Se vuoi iniziare in piccolo, anche questi sono disponibili.
          </p>
        </div>

        <PricingGrid plans={plans} level={level} creating={creating}
                     onCheckout={checkout} ultimate={ultimate} />

        <div className="mt-8 max-w-md mx-auto rounded-2xl p-4 border bg-white/70 backdrop-blur"
             style={{ borderColor: "rgba(123,92,224,0.25)" }}
             data-testid="premium-terms-row">
          <TermsCheckbox language={locale} accepted={termsAccepted}
                         onChange={setTermsAccepted}
                         testId="premium-terms-checkbox" />
          {!termsAccepted ? (
            <p className="mt-2 text-[11.5px]" style={{ color: "#9E2A48" }}
               data-testid="premium-terms-block-hint">
              {ti18n.block_until_accept}
            </p>
          ) : null}
        </div>

        <div className="mt-12 flex items-center justify-center gap-2 text-xs text-brand-secondary">
          <ShieldCheck className="h-4 w-4 text-brand-green" />
          Pagamento sicuro tramite Stripe · Cancellazione in qualsiasi momento.
        </div>
      </div>
    </div>
  );
}
